
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from '@/hooks/use-toast';
import { CANDIDATE_STATUS, JOB_TYPES } from '@/types/enums';
import { formatJobType } from '@/utils/formatters';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { MultiSelect } from '@/components/ui/multi-select';
import { Plus, Pencil, Trash2, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod"
import { useAuthUsers } from '@/hooks/useAuthUsers';
import { useAuthorization } from "@/hooks/useAuthorization";
import * as z from "zod"

interface JobPosition {
  id: string;
  role_name: string;
  job_type: string;
  description?: string;
  key_skills?: string[];
  package_offered?: string;
  target_hires: number;
  ai_evaluation_prompt?: string;
  interviewer?: string[] | string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const jobPositionSchema = z.object({
  role_name: z.string().min(2, {
    message: "Role name must be at least 2 characters.",
  }),
  job_type: z.string().refine(value => Object.values(JOB_TYPES).includes(value as any), {
    message: "Invalid job type.",
  }),
  description: z.string().optional(),
  key_skills: z.string().optional(),
  package_offered: z.string().optional(),
  target_hires: z.coerce.number().int().min(0, {
    message: 'Target hires must be 0 or more.',
  }),
  ai_evaluation_prompt: z.string().optional(),
  interviewer: z.array(z.string()).optional(),
  is_active: z.boolean().default(true),
})

type JobPositionForm = z.infer<typeof jobPositionSchema>

const AUTO_ASSIGN_PANELIST_STATUSES = [
  CANDIDATE_STATUS.PASSED,
  CANDIDATE_STATUS.PENDING,
  CANDIDATE_STATUS.HOLD,
  CANDIDATE_STATUS.SELECTED,
  CANDIDATE_STATUS.OFFERED,
  CANDIDATE_STATUS.ACCEPTED,
  CANDIDATE_STATUS.JOINED,
];

export default function JobPositionsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<JobPosition | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { hasPermission, loading } = useAuthorization();
  const canRead = hasPermission('job_positions.read');
  const canWrite = hasPermission('job_positions.write');
  const normalizeInterviewerValue = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  };

  const form = useForm<JobPositionForm>({
    resolver: zodResolver(jobPositionSchema),
    defaultValues: {
      role_name: "",
      job_type: JOB_TYPES.PROGRAMMING,
      description: "",
      key_skills: "",
      package_offered: "",
      target_hires: 0,
      ai_evaluation_prompt: "",
      interviewer: [],
      is_active: true,
    },
    mode: "onChange",
  })

  useEffect(() => {
    if (dialogOpen && !editingPosition) {
      form.reset();
    }
  }, [dialogOpen, editingPosition, form]);

  const { data: jobPositions, isLoading, isError } = useQuery({
    queryKey: ['job-positions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as JobPosition[] || [];
    },
    enabled: canRead,
  });

  const { data: authUsers } = useAuthUsers();

  const { data: questionGroups = [] } = useQuery({
    queryKey: ['question-groups', 'with-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('question_groups')
        .select('id, questions_per_test, must_have_questions, job_position_ids');
      if (error) throw error;
      return data as any[];
    },
    enabled: canRead,
  });

  const { data: questions = [] } = useQuery({
    queryKey: ['questions', 'position-mapping'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('questions')
        .select('id, group_id, job_position_ids, is_enabled, is_must_have');
      if (error) throw error;
      return data as any[];
    },
    enabled: canRead,
  });

  const questionCountMap = useMemo(() => {
    const counts: Record<string, number> = {};

    const groupCounts: Record<string, number> = {};
    questions.forEach((q: any) => {
      if (!q.group_id || q.is_enabled === false) return;
      groupCounts[q.group_id] = (groupCounts[q.group_id] || 0) + 1;
    });

    questionGroups.forEach((g: any) => {
      const enabledCount = groupCounts[g.id] || 0;
      const limit = g.questions_per_test || 0;
      const counted = Math.min(enabledCount, limit || enabledCount);
      (g.job_position_ids || []).forEach((id: string) => {
        counts[id] = (counts[id] || 0) + counted;
      });
    });

    questions.forEach((q: any) => {
      if (q.is_enabled === false) return;
      (q.job_position_ids || []).forEach((id: string) => {
        const group = questionGroups.find((g: any) => g.id === q.group_id);
        if (!group || !(group.job_position_ids || []).includes(id)) {
          counts[id] = (counts[id] || 0) + 1;
        }
      });
    });
    return counts;
  }, [questionGroups, questions]);

  const userMap = useMemo(() => {
    const map: Record<string, string> = {};
    authUsers?.users.forEach(u => {
      if (u.id) map[u.id] = u.email || u.id;
    });
    return map;
  }, [authUsers]);

  const assignDefaultInterviewersToCandidates = async (jobPositionId: string, interviewerIds: string[]) => {
    if (!jobPositionId || interviewerIds.length === 0) return;

    const { error } = await supabase
      .from('candidates')
      .update({ interviewer: interviewerIds } as never)
      .eq('job_position_id', jobPositionId)
      .is('interviewer', null)
      .in('status', AUTO_ASSIGN_PANELIST_STATUSES as any);

    if (error) throw error;
  };

  const createMutation = useMutation({
    mutationFn: async (data: JobPositionForm) => {
      const skillsArray = data.key_skills ? data.key_skills.split(',').map(s => s.trim()).filter(Boolean) : [];
      
      const payload = {
        role_name: data.role_name,
        job_type: data.job_type,
        description: data.description,
        key_skills: skillsArray,
        package_offered: data.package_offered,
        target_hires: data.target_hires,
        ai_evaluation_prompt: data.ai_evaluation_prompt,
        interviewer: data.interviewer && data.interviewer.length > 0 ? data.interviewer : null,
        is_active: data.is_active,
      };

      const { error } = await supabase
        .from('job_positions')
        .insert(payload as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-positions'] });
      toast({
        title: "Success",
        description: "Job position created successfully.",
      });
      setDialogOpen(false);
      setEditingPosition(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: JobPositionForm) => {
      if (!editingPosition) return;
      
      const skillsArray = data.key_skills ? data.key_skills.split(',').map(s => s.trim()).filter(Boolean) : [];
      
      const payload = {
        role_name: data.role_name,
        job_type: data.job_type,
        description: data.description,
        key_skills: skillsArray,
        package_offered: data.package_offered,
        target_hires: data.target_hires,
        ai_evaluation_prompt: data.ai_evaluation_prompt,
        interviewer: data.interviewer && data.interviewer.length > 0 ? data.interviewer : null,
        is_active: data.is_active,
      };

      const normalizedInterviewers = data.interviewer && data.interviewer.length > 0 ? data.interviewer : [];

      const { error } = await supabase
        .from('job_positions')
        .update(payload as never)
        .eq('id', editingPosition.id);
      if (error) throw error;

      await assignDefaultInterviewersToCandidates(editingPosition.id, normalizedInterviewers);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-positions'] });
      toast({
        title: "Success",
        description: "Job position updated successfully.",
      });
      setDialogOpen(false);
      setEditingPosition(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('job_positions')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-positions'] });
      toast({
        title: "Success",
        description: "Job position deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: JobPositionForm) => {
    
    if (editingPosition) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this job position?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (position: JobPosition) => {
    setEditingPosition(position);
    form.reset({
      role_name: position.role_name,
      job_type: position.job_type as any,
      description: position.description || "",
      key_skills: position.key_skills ? position.key_skills.join(", ") : "",
      package_offered: position.package_offered || "",
      target_hires: position.target_hires ?? 0,
      ai_evaluation_prompt: position.ai_evaluation_prompt || "",
      interviewer: normalizeInterviewerValue(position.interviewer),
      is_active: position.is_active,
    });
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingPosition(null);
      form.reset();
    }
  };

  const handleOpenDialog = () => {
    setEditingPosition(null);
    form.reset();
    setDialogOpen(true);
  };
  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!canRead) {
    return <div className="p-4">You do not have permission to view this page.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Job Positions</h1>
          <p className="text-muted-foreground">Manage job positions and their details</p>
        </div>
        {canWrite && (
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Job Position
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPosition ? 'Edit Job Position' : 'Create Job Position'}</DialogTitle>
                <DialogDescription>
                  {editingPosition ? 'Update the job position details.' : 'Create a new job position.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="role_name">Role Name</Label>
                <Input
                  id="role_name"
                  placeholder="Enter role name"
                  {...form.register("role_name")}
                />
                {form.formState.errors.role_name && (
                  <p className="text-sm text-red-500">{form.formState.errors.role_name.message}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="job_type">Job Type</Label>
                <Select onValueChange={value => form.setValue("job_type", value)} value={form.watch("job_type")}>
                  <SelectTrigger id="job_type">
                    <SelectValue placeholder="Select job type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(JOB_TYPES).map((type) => (
                      <SelectItem key={type} value={type}>
                        {formatJobType(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.job_type && (
                  <p className="text-sm text-red-500">{form.formState.errors.job_type.message}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="package_offered">Package Offered</Label>
                <Input
                  id="package_offered"
                  placeholder="3-5 LPA"
                  {...form.register("package_offered")}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="target_hires">Target Hires</Label>
                <Input
                  id="target_hires"
                  type="number"
                  min={0}
                  placeholder="Enter target hires"
                  {...form.register("target_hires", { valueAsNumber: true })}
                />
                {form.formState.errors.target_hires && (
                  <p className="text-sm text-red-500">{form.formState.errors.target_hires.message}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter job description"
                  {...form.register("description")}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="key_skills">Key Skills (comma separated)</Label>
                <Input
                  id="key_skills"
                  placeholder="React, TypeScript, Node.js"
                  {...form.register("key_skills")}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="ai_evaluation_prompt">AI Evaluation Prompt</Label>
                <Textarea
                  id="ai_evaluation_prompt"
                  placeholder="Enter AI evaluation prompt"
                  {...form.register("ai_evaluation_prompt")}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="interviewer">Interviewers</Label>
                <MultiSelect
                  options={authUsers?.users.map(u => ({ label: u.email || u.id, value: u.id })) ?? []}
                  value={normalizeInterviewerValue(form.watch('interviewer'))}
                  onChange={value => form.setValue('interviewer', value)}
                  placeholder="Select interviewers"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Label htmlFor="is_active">Is Active</Label>
                <Switch
                  id="is_active"
                  checked={form.watch("is_active")}
                  onCheckedChange={(checked) => form.setValue("is_active", checked)}
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Loading...' : 'Save changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Job Positions List</CardTitle>
          <CardDescription>View and manage existing job positions</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div>Loading job positions...</div>
          ) : isError ? (
            <div>Error loading job positions.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Role Name</TableHead>
                      <TableHead>Job Type</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Target Hires</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Questions</TableHead>
                    <TableHead>Interviewers</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobPositions?.map((position) => (
                    <TableRow key={position.id}>
                      <TableCell className="font-medium">{position.role_name}</TableCell>
                      <TableCell>{formatJobType(position.job_type)}</TableCell>
                      <TableCell>{position.package_offered || 'N/A'}</TableCell>
                      <TableCell>{position.target_hires ?? 0}</TableCell>
                      <TableCell>{position.description}</TableCell>
                      <TableCell>{questionCountMap[position.id] ?? 0}</TableCell>
                      <TableCell>
                        {normalizeInterviewerValue(position.interviewer).length > 0
                          ? normalizeInterviewerValue(position.interviewer).map((id) => userMap[id] ?? id).join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell>{position.is_active ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell className="text-right flex max-md:flex-col">
                        {canWrite && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(position)}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(position.id)}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/admin/job-positions/${position.id}/simulate`)}
                          title="Simulate"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
